<template>
  <div class="card">
    <!-- HEADER -->

    <div class="top-row">
      <div class="title-wrap">
        <div class="title">
          {{ post.title }}
        </div>

        <div class="meta-row">
          <span class="meta-pill">
            <span
              class="dot"
              :class="'dot-' + safeFreelanceType"
            />

            {{ prettyFreelanceType }}
          </span>

          <span
            v-if="post.contactType"
            class="meta-pill meta-pill-contact"
          >
            <img
              v-if="contactIcon"
              :src="contactIcon"
              class="meta-icon"
              alt=""
            />

            {{ prettyContactType }}
          </span>
        </div>
      </div>

      <div
        class="card-accent"
        aria-hidden="true"
      >
        <span class="accent-dot" />
      </div>
    </div>

    <!-- TEXT -->

    <div class="text">
      {{ post.cleanText }}
    </div>

    <!-- TAGS -->

    <div
      v-if="post.hashtags?.length"
      class="hashtags"
    >
      <span
        v-for="tag in post.hashtags.slice(0, 5)"
        :key="tag"
        class="tag"
      >
        {{ tag }}
      </span>
    </div>

    <!-- ACTION -->

    <button
      class="action-btn"
      type="button"
      :disabled="!post.actionUrl"
      @click.stop="openLink"
    >
      <img
        v-if="contactIcon"
        :src="contactIcon"
        class="action-icon"
        alt=""
      />

      <span class="action-text">
        Откликнуться
      </span>

      <span
        class="arrow"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M5 10H15M11 6L15 10L11 14"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </button>
  </div>
</template>

<script setup>
import { computed } from "vue";

import freelanceTg from "../assets/freelance_tg.png";
import freelanceEmail from "../assets/freelance_email.png";
import freelanceGoogle from "../assets/freelance_google.png";
import freelanceYandex from "../assets/freelance_yandex.png";

const props = defineProps({
  post: {
    type: Object,
    required: true
  }
});

const contactIcon = computed(() => {
  switch (props.post.contactType) {
    case "telegram":
      return freelanceTg;

    case "email":
      return freelanceEmail;

    case "google":
      return freelanceGoogle;

    case "yandex":
      return freelanceYandex;

    default:
      return null;
  }
});

const safeFreelanceType = computed(() => {
  return (
    props.post.freelanceType ||
    "other"
  ).toLowerCase();
});

const prettyFreelanceType = computed(() => {
  return safeFreelanceType.value;
});

const prettyContactType = computed(() => {
  const type = (
    props.post.contactType ||
    ""
  ).toLowerCase();

  if (!type) {
    return "контакт";
  }

  return type;
});

function openLink() {
  if (!props.post.actionUrl) {
    return;
  }

  window.open(
    props.post.actionUrl,
    "_blank",
    "noopener,noreferrer"
  );
}
</script>

<style scoped>
/* ========================================================= */
/* CARD */
/* ========================================================= */

.card {
  position: relative;

  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 11px;

  padding:
    16px;

  overflow: hidden;

  border:
    1px solid
    rgba(30, 45, 65, 0.06);

  border-radius:
    18px;

  background:
    linear-gradient(
      145deg,
      #ffffff 0%,
      #fafbfd 100%
    );

  box-shadow:
    0 7px 20px
    rgba(20, 30, 45, 0.045);

  cursor: pointer;

  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.card:hover {
  transform:
    translateY(-2px);

  border-color:
    rgba(40, 135, 247, 0.13);

  box-shadow:
    0 11px 26px
    rgba(20, 30, 45, 0.075);
}

/* ========================================================= */
/* HEADER */
/* ========================================================= */

.top-row {
  position: relative;
  z-index: 2;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 10px;
}

.title-wrap {
  min-width: 0;

  flex: 1;
}

/* ========================================================= */
/* TITLE */
/* ========================================================= */

.title {
  max-width: 100%;

  overflow: hidden;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 14px;
  font-weight: 700;
  line-height: 1.28;

  letter-spacing:
    -0.18px;

  color:
    #252a31;

  overflow-wrap:
    anywhere;
}

/* ========================================================= */
/* META */
/* ========================================================= */

.meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 6px;

  margin-top:
    8px;
}

.meta-pill {
  min-height: 24px;

  display: inline-flex;
  align-items: center;

  gap: 6px;

  padding:
    0 8px;

  border:
    1px solid
    rgba(30, 45, 65, 0.05);

  border-radius:
    8px;

  background:
    #f5f7f9;

  color:
    #727a84;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8.5px;
  line-height: 1;

  white-space:
    nowrap;
}

.meta-pill-contact {
  background:
    rgba(40, 135, 247, 0.045);

  border-color:
    rgba(40, 135, 247, 0.07);

  color:
    #607a99;
}

.meta-icon {
  width: 13px;
  height: 13px;

  flex-shrink: 0;

  object-fit: contain;
}

/* ========================================================= */
/* TYPE DOT */
/* ========================================================= */

.dot {
  width: 5px;
  height: 5px;

  flex-shrink: 0;

  border-radius:
    50%;

  background:
    #826ad0;
}

.dot-design {
  background:
    #27b782;
}

.dot-marketing {
  background:
    #3d89e9;
}

.dot-copywriting {
  background:
    #e6a23a;
}

.dot-video {
  background:
    #e76464;
}

.dot-other {
  background:
    #826ad0;
}

/* ========================================================= */
/* DECORATION */
/* ========================================================= */

.card-accent {
  width: 24px;
  height: 24px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius:
    8px;

  background:
    rgba(40, 135, 247, 0.055);
}

.accent-dot {
  width: 6px;
  height: 6px;

  border-radius:
    50%;

  background:
    #2887f7;

  box-shadow:
    0 0 0 4px
    rgba(40, 135, 247, 0.08);
}

/* ========================================================= */
/* TEXT */
/* ========================================================= */

.text {
  position: relative;
  z-index: 2;

  white-space:
    pre-wrap;

  overflow-wrap:
    anywhere;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10.5px;
  font-weight: 400;
  line-height: 1.5;

  letter-spacing:
    -0.01px;

  color:
    #666d76;
}

/* ========================================================= */
/* TAGS */
/* ========================================================= */

.hashtags {
  position: relative;
  z-index: 2;

  display: flex;
  flex-wrap: wrap;

  gap: 5px;
}

.tag {
  min-height: 23px;

  display: inline-flex;
  align-items: center;

  padding:
    0 8px;

  border:
    1px solid
    rgba(40, 135, 247, 0.07);

  border-radius:
    8px;

  background:
    rgba(40, 135, 247, 0.05);

  color:
    #4e7bac;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
  line-height: 1;

  overflow-wrap:
    anywhere;
}

/* ========================================================= */
/* ACTION */
/* ========================================================= */

.action-btn {
  position: relative;
  z-index: 2;

  width: 100%;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  margin-top:
    2px;

  padding:
    0 13px;

  border:
    1px solid
    rgba(40, 135, 247, 0.13);

  border-radius:
    11px;

  background:
    linear-gradient(
      135deg,
      #358cf3 0%,
      #2978d9 100%
    );

  color:
    #ffffff;

  cursor: pointer;

  box-shadow:
    0 5px 14px
    rgba(40, 120, 215, 0.16);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
  font-weight: 500;
  line-height: 1;

  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease,
    opacity 0.16s ease;
}

.action-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 8px 18px
    rgba(40, 120, 215, 0.21);
}

.action-btn:active:not(:disabled) {
  transform:
    translateY(0)
    scale(0.99);
}

.action-btn:disabled {
  opacity: 0.4;

  cursor:
    not-allowed;

  box-shadow: none;
}

.action-icon {
  width: 14px;
  height: 14px;

  flex-shrink: 0;

  object-fit: contain;
}

.arrow {
  width: 15px;
  height: 15px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-left:
    1px;

  opacity: 0.85;

  transition:
    transform 0.16s ease;
}

.arrow svg {
  width: 14px;
  height: 14px;
}

.action-btn:hover:not(:disabled)
.arrow {
  transform:
    translateX(2px);
}

/* ========================================================= */
/* DARK */
/* ========================================================= */

html[data-theme="dark"] .card {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border-color:
    rgba(255, 255, 255, 0.055);

  box-shadow:
    0 8px 22px
    rgba(0, 0, 0, 0.16);
}

html[data-theme="dark"] .card:hover {
  border-color:
    rgba(75, 150, 255, 0.13);

  box-shadow:
    0 12px 28px
    rgba(0, 0, 0, 0.23);
}

html[data-theme="dark"] .title {
  color:
    #eceef1;
}

html[data-theme="dark"] .text {
  color:
    #a5abb3;
}

html[data-theme="dark"] .meta-pill {
  background:
    rgba(255, 255, 255, 0.04);

  border-color:
    rgba(255, 255, 255, 0.045);

  color:
    #8f969f;
}

html[data-theme="dark"]
.meta-pill-contact {
  background:
    rgba(40, 135, 247, 0.06);

  border-color:
    rgba(75, 150, 255, 0.07);

  color:
    #8eadd0;
}

html[data-theme="dark"] .card-accent {
  background:
    rgba(40, 135, 247, 0.07);
}

html[data-theme="dark"] .accent-dot {
  background:
    #69aefc;

  box-shadow:
    0 0 0 4px
    rgba(40, 135, 247, 0.09);
}

html[data-theme="dark"] .tag {
  background:
    rgba(40, 135, 247, 0.065);

  border-color:
    rgba(70, 150, 255, 0.07);

  color:
    #82a9d3;
}

html[data-theme="dark"] .action-btn {
  background:
    linear-gradient(
      135deg,
      #337fd6 0%,
      #2967ad 100%
    );

  border-color:
    rgba(90, 160, 255, 0.08);

  box-shadow:
    0 6px 16px
    rgba(0, 0, 0, 0.2);
}

/* ========================================================= */
/* MOBILE */
/* ========================================================= */

@media (max-width: 420px) {
  .card {
    gap: 10px;

    padding:
      14px;

    border-radius:
      16px;
  }

  .title {
    font-size:
      13px;
  }

  .text {
    font-size:
      10px;
  }

  .meta-pill {
    min-height:
      23px;

    font-size:
      8px;
  }

  .tag {
    min-height:
      22px;

    font-size:
      7.5px;
  }

  .action-btn {
    height:
      37px;

    font-size:
      9.5px;
  }
}

/* ========================================================= */
/* REDUCED MOTION */
/* ========================================================= */

@media (prefers-reduced-motion: reduce) {
  .card,
  .action-btn,
  .arrow {
    transition: none;
  }

  .card:hover,
  .action-btn:hover:not(:disabled) {
    transform: none;
  }
}
</style>